/**
 * Google reCAPTCHA v3 — invisible bot protection.
 *
 * Loads the script lazily on first use so we don't pay the network cost
 * for unauthenticated visitors who never touch the auth flow.
 *
 * Usage:
 *   const token = await executeRecaptcha("signup");
 *   await api.post("/auth/register", { ...form, recaptcha_token: token });
 *
 * If REACT_APP_RECAPTCHA_SITE_KEY is not set, this no-ops and returns
 * `null` — the backend treats a missing token as a soft-pass when its
 * own RECAPTCHA_SECRET_KEY isn't configured, so dev/preview keep
 * working without spamming Google's quota.
 */
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
let scriptPromise = null;

function loadScript() {
  if (scriptPromise) return scriptPromise;
  if (!SITE_KEY) {
    scriptPromise = Promise.resolve(null);
    return scriptPromise;
  }
  if (typeof window === "undefined") {
    scriptPromise = Promise.resolve(null);
    return scriptPromise;
  }
  if (window.grecaptcha?.execute) {
    scriptPromise = Promise.resolve(window.grecaptcha);
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // grecaptcha.ready is the canonical wait — fires once the v3 runtime
      // has finished initializing. Without this, execute() can throw.
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
      } else {
        resolve(window.grecaptcha || null);
      }
    };
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export async function executeRecaptcha(action) {
  if (!SITE_KEY) return null;
  try {
    const grecaptcha = await loadScript();
    if (!grecaptcha?.execute) return null;
    return await grecaptcha.execute(SITE_KEY, { action });
  } catch (e) {
    // Network failure / blocked-extension / time-out: return null and let
    // the backend's soft-fail mode allow the request through. Hard-fail
    // policy lives server-side via RECAPTCHA_REQUIRED.
    // eslint-disable-next-line no-console
    console.warn("[recaptcha] execute failed:", e);
    return null;
  }
}

/**
 * Hide the reCAPTCHA badge with a CSS rule the badge respects per
 * Google's terms (they require equivalent attribution in the UI). Apply
 * sparingly — call only on screens that show the badge attribution
 * inline (auth screens display it under the submit button).
 */
export function hideRecaptchaBadge() {
  if (typeof document === "undefined") return;
  if (document.getElementById("link-recaptcha-badge-style")) return;
  const style = document.createElement("style");
  style.id = "link-recaptcha-badge-style";
  style.textContent = ".grecaptcha-badge { visibility: hidden !important; }";
  document.head.appendChild(style);
}
